// frontend/src/components/public/blocks/MediaReachBlock.tsx
import React, { useEffect, useState, useRef } from 'react';
import { Box, Typography, Grid, Paper } from '@mui/material';
import {
  Campaign as CampaignIcon,
  Instagram as InstagramIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  YouTube as YouTubeIcon,
  LinkedIn as LinkedInIcon,
  Public as PublicIcon,
  Article as ArticleIcon,
  Email as EmailIcon,
  OpenInNew as OpenIcon,
  TrendingUp as TrendingIcon,
  EuroSymbolTwoTone as MoneyIcon
} from '@mui/icons-material';

interface MediaReachBlockProps {
  data: {
    backgroundImage?: string;
    totalMonthlyReach: number;
    socialMedia: Array<{
      platform: string;
      followerCount: number;
    }>;
    pressCoverage: Array<{
      publication: string;
      mentionCount: number;
    }>;
    emailStats: {
      subscriberCount: number;
      openRate: number;
    };
    websiteTraffic: number;
    estimatedMediaValue: number;
    enableAnimations: boolean;
  };
 theme?: {
    primaryColor: string;
    accentColor: string;
    watermark?: {
      url: string;
      position: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
      opacity: number;
    };
  };
}

const MediaReachBlock: React.FC<MediaReachBlockProps> = ({ data, theme }) => {
  const [hasAnimated, setHasAnimated] = useState(false);
  const blockRef = useRef<HTMLDivElement>(null);
  const watermark = theme?.watermark;


  useEffect(() => {
    if (!data.enableAnimations) {
      setHasAnimated(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
        }
      },
      { threshold: 0.3 }
    );

    if (blockRef.current) {
      observer.observe(blockRef.current);
    }

    return () => observer.disconnect();
  }, [hasAnimated, data.enableAnimations]);

  const getPlatformIcon = (platform: string) => {
    const platformLower = platform.toLowerCase();
    if (platformLower.includes('instagram')) return <InstagramIcon sx={{ fontSize: 32 }} />;
    if (platformLower.includes('facebook')) return <FacebookIcon sx={{ fontSize: 32 }} />;
    if (platformLower.includes('twitter') || platformLower.includes('x')) return <TwitterIcon sx={{ fontSize: 32 }} />;
    if (platformLower.includes('youtube')) return <YouTubeIcon sx={{ fontSize: 32 }} />;
    if (platformLower.includes('linkedin')) return <LinkedInIcon sx={{ fontSize: 32 }} />;
    if (platformLower.includes('tiktok')) return <PublicIcon sx={{ fontSize: 32 }} />;
    return <PublicIcon sx={{ fontSize: 32 }} />;
  };

  return (
    <Box
      id="media-reach"
      ref={blockRef}
      sx={{
        position: 'relative',
        py: 8,
        px: 3,
        minHeight: '600px',
        overflow: 'hidden'
      }}
    >
      {/* Background Image with Overlay */}
      {data.backgroundImage && (
        <>
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `url(${data.backgroundImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              filter: 'blur(3px)',
              transform: 'scale(1.1)',
              zIndex: 0
            }}
          />
                {/* Watermark */}
      {watermark?.url && (
        <Box
          component="img"
          src={watermark.url}
          alt="Watermark"
          sx={{
            position: 'absolute',
            opacity: watermark.opacity ?? 0.15,
            width: { xs: '80px', sm: '100px', md: '120px' },
            pointerEvents: 'none',
            zIndex: 1.5,
            ...(watermark.position === 'top-left' && { top: 16, left: 16 }),
            ...(watermark.position === 'top-right' && { top: 16, right: 16 }),
            ...(watermark.position === 'bottom-left' && { bottom: 16, left: 16 }),
            ...(watermark.position === 'bottom-right' && { bottom: 16, right: 16 })
          }}
        />
      )}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(0, 0, 0, 0.65)',
              zIndex: 1
            }}
          />
        </>
      )}

      {/* Content */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          maxWidth: 1200,
          mx: 'auto'
        }}
      >
        {/* Header with Total Reach */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <CampaignIcon
            sx={{
              fontSize: 64,
              color: theme?.primaryColor || '#3A9188',
              mb: 2
            }}
          />
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              color: data.backgroundImage ? '#ffffff' : '#2C3E50',
              mb: 2
            }}
          >
            Our Platform
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: data.backgroundImage ? 'rgba(255,255,255,0.8)' : '#666',
              mb: 4
            }}
          >
            Reaching audiences across multiple channels
          </Typography>

          {/* Hero Stat - Total Monthly Reach */}
          {data.totalMonthlyReach > 0 && (
            <Box
              sx={{
                display: 'inline-block',
                px: 6,
                py: 3,
                bgcolor: data.backgroundImage ? 'rgba(58, 145, 136, 0.2)' : theme?.primaryColor || '#3A9188',
                borderRadius: 3,
                border: data.backgroundImage ? `2px solid ${theme?.primaryColor || '#3A9188'}` : 'none'
              }}
            >
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 700,
                  color: '#ffffff',
                  mb: 1
                }}
              >
                <AnimatedNumber
                  value={data.totalMonthlyReach}
                  animate={hasAnimated}
                  duration={2000}
                  sx={{ fontSize: '3.5rem', fontWeight: 700 }}
                />
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: 'rgba(255,255,255,0.9)',
                  textTransform: 'uppercase',
                  letterSpacing: 2
                }}
              >
                Monthly Impressions
              </Typography>
            </Box>
          )}
        </Box>

        {/* Social Media Section */}
        {data.socialMedia.length > 0 && (
          <Box sx={{ mb: 6 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: data.backgroundImage ? '#ffffff' : '#2C3E50',
                mb: 3,
                textAlign: 'center'
              }}
            >
              Social Media Reach
            </Typography>
            <Grid container spacing={3}>
              {data.socialMedia.map((item, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Paper
                    elevation={data.backgroundImage ? 0 : 2}
                    sx={{
                      p: 3,
                      textAlign: 'center',
                      bgcolor: data.backgroundImage ? 'rgba(255,255,255,0.1)' : '#f9f9f9',
                      backdropFilter: data.backgroundImage ? 'blur(10px)' : 'none',
                      border: data.backgroundImage ? '1px solid rgba(255,255,255,0.2)' : 'none',
                      borderRadius: 2,
                      transition: 'transform 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        bgcolor: data.backgroundImage ? 'rgba(255,255,255,0.15)' : '#ffffff'
                      }
                    }}
                  >
                    <Box sx={{ color: theme?.primaryColor || '#3A9188', mb: 2 }}>
                      {getPlatformIcon(item.platform)}
                    </Box>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        color: data.backgroundImage ? '#ffffff' : '#2C3E50',
                        mb: 1
                      }}
                    >
                      <AnimatedNumber
                        value={item.followerCount}
                        animate={hasAnimated}
                        duration={2000}
                        sx={{ fontSize: '2rem', fontWeight: 700 }}
                      />
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: data.backgroundImage ? 'rgba(255,255,255,0.8)' : '#666',
                        fontWeight: 500
                      }}
                    >
                      {item.platform}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Press Coverage Section */}
        {data.pressCoverage.length > 0 && (
          <Box sx={{ mb: 6 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: data.backgroundImage ? '#ffffff' : '#2C3E50',
                mb: 3,
                textAlign: 'center'
              }}
            >
              Press Coverage
            </Typography>
            <Grid container spacing={3}>
              {data.pressCoverage.map((item, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Paper
                    elevation={data.backgroundImage ? 0 : 2}
                    sx={{
                      p: 3,
                      textAlign: 'center',
                      bgcolor: data.backgroundImage ? 'rgba(255,255,255,0.1)' : '#f9f9f9',
                      backdropFilter: data.backgroundImage ? 'blur(10px)' : 'none',
                      border: data.backgroundImage ? '1px solid rgba(255,255,255,0.2)' : 'none',
                      borderRadius: 2,
                      transition: 'transform 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        bgcolor: data.backgroundImage ? 'rgba(255,255,255,0.15)' : '#ffffff'
                      }
                    }}
                  >
                    <ArticleIcon
                      sx={{
                        fontSize: 32,
                        color: theme?.primaryColor || '#3A9188',
                        mb: 2
                      }}
                    />
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        color: data.backgroundImage ? '#ffffff' : '#2C3E50',
                        mb: 1
                      }}
                    >
                      <AnimatedNumber
                        value={item.mentionCount}
                        animate={hasAnimated}
                        duration={2000}
                        sx={{ fontSize: '2rem', fontWeight: 700 }}
                      />
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: data.backgroundImage ? 'rgba(255,255,255,0.8)' : '#666',
                        fontWeight: 500
                      }}
                    >
                      {item.publication}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Email & Web Stats Grid */}
        <Grid container spacing={3} sx={{ mb: 6 }}>
          {/* Email Subscribers */}
          {data.emailStats.subscriberCount > 0 && (
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={data.backgroundImage ? 0 : 2}
                sx={{
                  p: 3,
                  textAlign: 'center',
                  bgcolor: data.backgroundImage ? 'rgba(255,255,255,0.1)' : '#f9f9f9',
                  backdropFilter: data.backgroundImage ? 'blur(10px)' : 'none',
                  border: data.backgroundImage ? '1px solid rgba(255,255,255,0.2)' : 'none',
                  borderRadius: 2,
                  height: '100%'
                }}
              >
                <EmailIcon
                  sx={{
                    fontSize: 40,
                    color: theme?.primaryColor || '#3A9188',
                    mb: 1
                  }}
                />
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: data.backgroundImage ? '#ffffff' : '#2C3E50',
                    mb: 1
                  }}
                >
                  <AnimatedNumber
                    value={data.emailStats.subscriberCount}
                    animate={hasAnimated}
                    duration={2000}
                    sx={{ fontSize: '2rem', fontWeight: 700 }}
                  />
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: data.backgroundImage ? 'rgba(255,255,255,0.8)' : '#666',
                    textTransform: 'uppercase',
                    letterSpacing: 1
                  }}
                >
                  Email Subscribers
                </Typography>
              </Paper>
            </Grid>
          )}

          {/* Open Rate */}
          {data.emailStats.openRate > 0 && (
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={data.backgroundImage ? 0 : 2}
                sx={{
                  p: 3,
                  textAlign: 'center',
                  bgcolor: data.backgroundImage ? 'rgba(255,255,255,0.1)' : '#f9f9f9',
                  backdropFilter: data.backgroundImage ? 'blur(10px)' : 'none',
                  border: data.backgroundImage ? '1px solid rgba(255,255,255,0.2)' : 'none',
                  borderRadius: 2,
                  height: '100%'
                }}
              >
                <OpenIcon
                  sx={{
                    fontSize: 40,
                    color: theme?.primaryColor || '#3A9188',
                    mb: 1
                  }}
                />
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: data.backgroundImage ? '#ffffff' : '#2C3E50',
                    mb: 1
                  }}
                >
                  <AnimatedNumber
                    value={data.emailStats.openRate}
                    animate={hasAnimated}
                    duration={2000}
                    suffix="%"
                    sx={{ fontSize: '2rem', fontWeight: 700 }}
                  />
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: data.backgroundImage ? 'rgba(255,255,255,0.8)' : '#666',
                    textTransform: 'uppercase',
                    letterSpacing: 1
                  }}
                >
                  Open Rate
                </Typography>
              </Paper>
            </Grid>
          )}

          {/* Website Traffic */}
          {data.websiteTraffic > 0 && (
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={data.backgroundImage ? 0 : 2}
                sx={{
                  p: 3,
                  textAlign: 'center',
                  bgcolor: data.backgroundImage ? 'rgba(255,255,255,0.1)' : '#f9f9f9',
                  backdropFilter: data.backgroundImage ? 'blur(10px)' : 'none',
                  border: data.backgroundImage ? '1px solid rgba(255,255,255,0.2)' : 'none',
                  borderRadius: 2,
                  height: '100%'
                }}
              >
                <TrendingIcon
                  sx={{
                    fontSize: 40,
                    color: theme?.primaryColor || '#3A9188',
                    mb: 1
                  }}
                />
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: data.backgroundImage ? '#ffffff' : '#2C3E50',
                    mb: 1
                  }}
                >
                  <AnimatedNumber
                    value={data.websiteTraffic}
                    animate={hasAnimated}
                    duration={2000}
                    sx={{ fontSize: '2rem', fontWeight: 700 }}
                  />
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: data.backgroundImage ? 'rgba(255,255,255,0.8)' : '#666',
                    textTransform: 'uppercase',
                    letterSpacing: 1
                  }}
                >
                  Monthly Visitors
                </Typography>
              </Paper>
            </Grid>
          )}

          {/* Estimated Media Value */}
          {data.estimatedMediaValue > 0 && (
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={data.backgroundImage ? 0 : 2}
                sx={{
                  p: 3,
                  textAlign: 'center',
                  bgcolor: data.backgroundImage ? 'rgba(255,255,255,0.1)' : '#f9f9f9',
                  backdropFilter: data.backgroundImage ? 'blur(10px)' : 'none',
                  border: data.backgroundImage ? '1px solid rgba(255,255,255,0.2)' : 'none',
                  borderRadius: 2,
                  height: '100%'
                }}
              >
                <MoneyIcon
                  sx={{
                    fontSize: 40,
                    color: theme?.primaryColor || '#3A9188',
                    mb: 1
                  }}
                />
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: data.backgroundImage ? '#ffffff' : '#2C3E50',
                    mb: 1
                  }}
                >
                  <AnimatedNumber
                    value={data.estimatedMediaValue}
                    animate={hasAnimated}
                    duration={2000}
                    prefix="€"
                    sx={{ fontSize: '2rem', fontWeight: 700 }}
                  />
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: data.backgroundImage ? 'rgba(255,255,255,0.8)' : '#666',
                    textTransform: 'uppercase',
                    letterSpacing: 1
                  }}
                >
                  Media Value
                </Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      </Box>
    </Box>
  );
};

// Animated Number Component
interface AnimatedNumberProps {
  value: number;
  animate: boolean;
  duration?: number;
  prefix?: string;
  suffix?: string;
  sx?: any;
}

const AnimatedNumber: React.FC<AnimatedNumberProps> = ({
  value,
  animate,
  duration = 2000,
  prefix = '',
  suffix = '',
  sx
}) => {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    if (!animate) {
      setDisplayValue(value);
      return;
    }

    const startTime = Date.now();
    const endTime = startTime + duration;

    const updateValue = () => {
      const now = Date.now();
      const progress = Math.min((now - startTime) / duration, 1);
      
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const current = Math.floor(easeOutQuart * value);
      
      setDisplayValue(current);

      if (now < endTime) {
        requestAnimationFrame(updateValue);
      } else {
        setDisplayValue(value);
      }
    };

    requestAnimationFrame(updateValue);
  }, [animate, value, duration]);

  return (
    <Typography component="span" sx={sx}>
      {prefix}{displayValue.toLocaleString()}{suffix}
    </Typography>
  );
};

export default MediaReachBlock;